import React, { useReducer } from "react";
import { useTranslation } from "react-i18next";
import { HoverTooltip } from "../common/HoverTooltip";
import { formatRelativeTime } from "./ActivityLogsHelperFunctions";

interface RelativeTimeTooltipProps {
    /** Raw ISO datetime (UTC) the relative label is computed from. */
    isoDatetime: string;
    /** The already-formatted absolute timestamp to display. */
    children: React.ReactNode;
}

/**
 * Wraps an absolute timestamp and shows its human-friendly age ("5 minutes
 * ago") in a tooltip. The label is derived during render, so it always tracks
 * the current isoDatetime and i18n.language. HoverTooltip renders its content
 * even while hidden, so mouse-enter/focus force a re-render — the label is
 * recomputed against the current time before the tooltip becomes visible,
 * keeping the value fresh without timers or changes to the shared
 * HoverTooltip.
 */
export const RelativeTimeTooltip: React.FC<RelativeTimeTooltipProps> = ({ isoDatetime, children }) => {
    const { i18n } = useTranslation();
    const [, refresh] = useReducer((n: number) => n + 1, 0);
    const label = formatRelativeTime(isoDatetime, i18n.language);

    // An unparseable isoDatetime produces an empty label (see formatRelativeTime).
    // There's nothing meaningful to show in a tooltip in that case, so skip
    // HoverTooltip entirely and render the plain timestamp children.
    if (!label) {
        return (
            <span data-testid="RelativeTimeTooltip-trigger">
                {children}
            </span>
        );
    }

    return (
        <span
            data-testid="RelativeTimeTooltip-trigger"
            onMouseEnter={refresh}
            onFocus={refresh}
        >
            {/*
                content is deliberately a node (not the bare `label` string): HoverTooltip
                falls back to `aria-label={content}` only when content is a string, and that
                aria-label would sit on the focusable wrapper span and override the visible
                timestamp text in the accessible-name computation. Wrapping in a fragment
                keeps the relative time reaching assistive tech solely via aria-describedby,
                which is the correct ARIA tooltip pattern (name = timestamp, description =
                relative time).
            */}
            <HoverTooltip content={<>{label}</>}>
                <span>{children}</span>
            </HoverTooltip>
        </span>
    );
};
